import AlbInput from "@components/common/AlbInput";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Grid } from "@mui/material";
import type { TFunction } from "i18next";
import type { SubmitHandler } from "react-hook-form";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import type { MemberFormSchemaType } from "@/schemas/organization-form/member.schema";

import {
  MemberFormSchema,
  MemberFormSchemaDefaultValues,
} from "@/schemas/organization-form/member.schema";

type TMemberForm = {
  unUsedFields?: Array<keyof typeof MemberFormSchema.shape>;
  disabledFields?: Array<keyof typeof MemberFormSchema.shape>;
  onSubmit: SubmitHandler<MemberFormSchemaType>;
  values?: MemberFormSchemaType;
  label?: string;
};
function MemberForm({
  unUsedFields,
  disabledFields,
  onSubmit,
  values = MemberFormSchemaDefaultValues,
  label = "Ajouter",
}: TMemberForm) {
  const { t } = useTranslation();
  const methods = useForm<MemberFormSchemaType>({
    mode: "onChange",
    resolver: zodResolver(MemberFormSchema),
    values,
  });
  const {
    formState: { isSubmitting },
    register,
  } = methods;
  const isFieldUnUsed = (name: keyof typeof MemberFormSchema.shape) =>
    unUsedFields?.includes(name) ?? false;
  const submit = async (data: MemberFormSchemaType) => {
    onSubmit(data);
  };
  return (
    <FormProvider {...methods}>
      <Grid
        container
        direction="row"
        spacing={2}
        paddingBottom={2}
        component="form"
        onSubmit={methods.handleSubmit(submit)}
      >
        {!isFieldUnUsed("id") ? (
          <input type="hidden" value={values.id} {...register("id")} />
        ) : null}

        <MemberFormUI disabledFields={disabledFields} t={t} />
        <Grid size={{ xs: 12 }}>
          <Button
            type="submit"
            size="large"
            variant="contained"
            fullWidth
            disabled={isSubmitting}
            sx={{ mt: 4 }}
          >
            {label}
          </Button>
        </Grid>
      </Grid>
    </FormProvider>
  );
}

export function MemberFormUI({
  disabledFields,
  t,
  name,
}: {
  disabledFields?: Array<keyof typeof MemberFormSchema.shape>;
  t: TFunction;
  name?: string;
}) {
  const isFieldDisabled = (name: keyof typeof MemberFormSchema.shape) =>
    disabledFields?.includes(name) ?? false;
  return (
    <>
      <Grid size={{ xs: 12, md: 6 }}>
        <AlbInput
          name={name ? `${name}.assigneeFirstName` : "assigneeFirstName"}
          label={t("common.form.input.first-name")}
          type="TextField"
          isEditAllowed={!isFieldDisabled("assigneeFirstName")}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <AlbInput
          name={name ? `${name}.assigneeLastName` : "assigneeLastName"}
          label={t("common.form.input.last-name")}
          type="TextField"
          isEditAllowed={!isFieldDisabled("assigneeLastName")}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <AlbInput
          name={name ? `${name}.assigneePhone` : "assigneePhone"}
          label={t("common.form.input.phone")}
          type="PhoneField"
          isEditAllowed={!isFieldDisabled("assigneePhone")}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <AlbInput
          name={name ? `${name}.assigneeEmail` : "assigneeEmail"}
          label={t("common.form.input.email")}
          type="TextField"
          inputType="email"
          isEditAllowed={!isFieldDisabled("assigneeEmail")}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <AlbInput
          name={name ? `${name}.assigneeFunction` : "assigneeFunction"}
          label={t("common.form.input.function")}
          type="TextField"
          isEditAllowed={!isFieldDisabled("assigneeFunction")}
        />
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <AlbInput
          name={name ? `${name}.assigneeService` : "assigneeService"}
          label={t("common.form.input.service")}
          type="TextField"
          isEditAllowed={!isFieldDisabled("assigneeService")}
        />
      </Grid>
    </>
  );
}
export default MemberForm;
